/**
 * Authentication operations.
 *
 * Wraps the Firebase SDK so components deal in plain results and friendly
 * messages rather than raw Firebase error codes.
 *
 * Provider linking (§24) is handled here: when Firebase reports that an
 * account already exists with a different sign-in method, we explain which
 * method to use and, after the user re-authenticates, link the new credential
 * to the *existing* Firebase account. That keeps one Firebase UID — and
 * therefore one MongoDB user and one RevenueCat customer.
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase.js';

/** Human-readable messages for the Firebase error codes users actually hit. */
const ERROR_MESSAGES = {
  'auth/invalid-email': 'That email address does not look right.',
  'auth/user-disabled': 'This account has been disabled. Contact support if you think this is wrong.',
  'auth/user-not-found': 'We could not find an account with that email address.',
  'auth/wrong-password': 'That password is not correct.',
  'auth/invalid-credential': 'That email address or password is not correct.',
  'auth/email-already-in-use': 'An account already exists with that email address.',
  'auth/weak-password': 'Choose a password with at least 6 characters.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/popup-closed-by-user': 'The sign-in window was closed before finishing.',
  'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups and try again.',
  'auth/cancelled-popup-request': 'Another sign-in window is already open.',
  'auth/network-request-failed': 'We could not reach the network. Check your connection.',
  'auth/requires-recent-login': 'Please sign in again before making this change.',
  'auth/expired-action-code': 'That link has expired. Request a new one.',
  'auth/invalid-action-code': 'That link is not valid. Request a new one.',
  'auth/operation-not-allowed': 'That sign-in method is not enabled for this project.',
  'auth/unauthorized-domain': 'This domain is not authorised for sign-in.',
};

export class AuthError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.details = details;
  }
}

function translate(error) {
  const code = error?.code ?? 'auth/unknown';
  const message = ERROR_MESSAGES[code] ?? 'Something went wrong signing you in. Please try again.';
  return new AuthError(code, message, { original: error?.message });
}

function requireAuth() {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new AuthError(
      'auth/not-configured',
      'Sign-in is not available on this deployment yet.',
    );
  }
  return auth;
}

/* ----------------------------------------------------------- email + password */

export async function registerWithEmail({ email, password, displayName }) {
  const auth = requireAuth();

  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }

    // Verification is sent immediately; the account works while unverified
    // unless REQUIRE_EMAIL_VERIFICATION is enabled on the API.
    await sendEmailVerification(credential.user).catch(() => {});

    return credential.user;
  } catch (error) {
    throw translate(error);
  }
}

export async function signInWithEmail({ email, password }) {
  const auth = requireAuth();

  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  } catch (error) {
    throw translate(error);
  }
}

export async function resendVerificationEmail() {
  const auth = requireAuth();
  if (!auth.currentUser) throw new AuthError('auth/no-user', 'You are not signed in.');

  try {
    await sendEmailVerification(auth.currentUser);
  } catch (error) {
    throw translate(error);
  }
}

export async function requestPasswordReset(email) {
  const auth = requireAuth();

  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    // Do not reveal whether an address is registered.
    if (error?.code === 'auth/user-not-found') return;
    throw translate(error);
  }
}

export async function completePasswordReset({ oobCode, newPassword }) {
  const auth = requireAuth();

  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (error) {
    throw translate(error);
  }
}

/** Changing a password requires a recent sign-in, so we reauthenticate first. */
export async function changePassword({ currentPassword, newPassword }) {
  const auth = requireAuth();
  const user = auth.currentUser;
  if (!user?.email) throw new AuthError('auth/no-user', 'You are not signed in.');

  try {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  } catch (error) {
    throw translate(error);
  }
}

/* ------------------------------------------------------------------ providers */

/**
 * Google sign-in.
 *
 * On `auth/account-exists-with-different-credential` we surface which provider
 * the account already uses so the UI can guide the user rather than dead-end.
 */
export async function signInWithGoogle() {
  const auth = requireAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    if (error?.code === 'auth/account-exists-with-different-credential') {
      throw await buildLinkingError(auth, error, 'Google');
    }
    throw translate(error);
  }
}

/** Sign in with Apple, via the Apple OAuth provider. */
export async function signInWithApple() {
  const auth = requireAuth();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');

  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    if (error?.code === 'auth/account-exists-with-different-credential') {
      throw await buildLinkingError(auth, error, 'Apple');
    }
    throw translate(error);
  }
}

/**
 * Builds an actionable error for the "same email, different provider" case.
 * The pending credential is carried so it can be linked after the user signs
 * in with the method they already have.
 */
async function buildLinkingError(auth, error, attemptedProvider) {
  const email = error?.customData?.email;
  let existingMethods = [];

  try {
    if (email) existingMethods = await fetchSignInMethodsForEmail(auth, email);
  } catch {
    // Non-fatal — we can still explain the situation without the method list.
  }

  const friendly = existingMethods
    .map((method) => {
      if (method === 'password') return 'email and password';
      if (method === 'google.com') return 'Google';
      if (method === 'apple.com') return 'Apple';
      return method;
    })
    .join(' or ');

  return new AuthError(
    'auth/account-exists-with-different-credential',
    friendly
      ? `You already have an account for ${email} using ${friendly}. ` +
        `Sign in that way first, and we will connect ${attemptedProvider} to it.`
      : `An account already exists for ${email} with a different sign-in method.`,
    {
      email,
      existingMethods,
      attemptedProvider,
      // Kept so linkPendingCredential can complete the flow.
      pendingCredential: OAuthProvider.credentialFromError(error),
    },
  );
}

/**
 * Links a previously-pending credential to the now signed-in account.
 *
 * Because this attaches to the existing Firebase account, the Firebase UID is
 * unchanged — so the MongoDB user and the RevenueCat customer stay the same.
 */
export async function linkPendingCredential(pendingCredential) {
  const auth = requireAuth();
  if (!auth.currentUser) throw new AuthError('auth/no-user', 'Sign in first.');
  if (!pendingCredential) return auth.currentUser;

  try {
    const result = await linkWithCredential(auth.currentUser, pendingCredential);
    return result.user;
  } catch (error) {
    if (error?.code === 'auth/credential-already-in-use') return auth.currentUser;
    throw translate(error);
  }
}

/* -------------------------------------------------------------------- session */

export async function signOut() {
  const auth = getFirebaseAuth();
  if (auth) await firebaseSignOut(auth);
}

/** Subscribes to auth state. Returns an unsubscribe function. */
export function subscribeToAuthState(callback) {
  const auth = getFirebaseAuth();
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

/** Reloads the Firebase user so `emailVerified` reflects a just-clicked link. */
export async function reloadUser() {
  const auth = getFirebaseAuth();
  if (!auth?.currentUser) return null;
  await auth.currentUser.reload();
  return auth.currentUser;
}
